import { extendTheme } from '@chakra-ui/react';
import colors from './foundations/colors';
import Button from './components/button';
import Table from './components/table';
import Modal from './components/modal';
import { Input, Textarea, Select } from './components/input';
import Card from './components/card';
import Menu from './components/menu';
import { installPopperHideGate } from '../lib/popperHideGate';

// 말풍선 숨김 규칙의 안전 스위치(html[data-popper-hide]) — 테마를 쓰는 모든 앱 루트(App·데모·미리보기)에서 한 번 설치된다.
// iOS 사파리에서 확대·키보드로 화면이 옮겨진 동안에는 popper 의 가려짐 판정이 틀려 숨김 규칙을 끈다(src/lib/popperHideGate.js).
installPopperHideGate();

const theme = extendTheme({
  fonts: {
    heading: `'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
    body: `'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
    mono: `source-code-pro, Menlo, Monaco, Consolas, monospace`,
  },

  colors,

  styles: {
    global: {
      'html, body': {
        wordBreak: 'keep-all',
        overflowWrap: 'break-word',
        bg: 'neutral.50',
        color: 'neutral.800',
        letterSpacing: '-0.01em',
      },
      // 2026-09-27 말풍선 전수조사: popper 는 트리거가 스크롤 상자 밖으로 완전히 나가면 이 속성만 달아 주고 숨기지는 않는다.
      // Portal 로 띄운 팝오버·툴팁·메뉴(채널 사이드바 정렬 팝오버 등)가 트리거 없이 떠 있지 않게 여기서 숨긴다.
      // 자손까지 !important — Chakra 팝오버 본문은 열릴 때 인라인 `visibility: visible` 을 달아 감싸는 층의 hidden 을 이긴다(최종 QA 실측).
      // html[data-popper-hide] 일 때만 — iOS 사파리 확대·키보드 상태에서는 popper 판정이 틀려 끈다(src/lib/popperHideGate.js).
      'html[data-popper-hide] [data-popper-reference-hidden], html[data-popper-hide] [data-popper-reference-hidden] *': {
        visibility: 'hidden !important',
        pointerEvents: 'none !important',
      },
    },
  },

  components: {
    Button,
    Table,
    Modal,
    Input,
    Textarea,
    Select,
    Card,
    Menu,

    Badge: {
      variants: {
        brand: {
          bg: 'brand.50',
          color: 'brand.700',
          borderRadius: '4px',
          fontWeight: '600',
          px: 2,
          py: 0.5,
          fontSize: 'xs',
        },
        admin: {
          bg: 'neutral.100',
          color: 'neutral.700',
          borderRadius: '4px',
          fontWeight: '500',
          px: 2,
          py: 0.5,
          fontSize: 'xs',
        },
        success: {
          bg: '#e8f5ef',
          color: 'status.success',
          borderRadius: '4px',
          fontWeight: '600',
          px: 2,
          py: 0.5,
          fontSize: 'xs',
        },
        warning: {
          bg: 'orange.50',
          color: 'orange.700',
          borderRadius: '4px',
          fontWeight: '600',
          px: 2,
          py: 0.5,
          fontSize: 'xs',
        },
        error: {
          bg: 'red.50',
          color: 'red.700',
          borderRadius: '4px',
          fontWeight: '600',
          px: 2,
          py: 0.5,
          fontSize: 'xs',
        },
        info: {
          bg: 'blue.50',
          color: 'blue.700',
          borderRadius: '4px',
          fontWeight: '600',
          px: 2,
          py: 0.5,
          fontSize: 'xs',
        },
        neutral: {
          bg: 'neutral.100',
          color: 'neutral.600',
          borderRadius: '4px',
          fontWeight: '500',
          px: 2,
          py: 0.5,
          fontSize: 'xs',
        },
      },
    },

    Tabs: {
      variants: {
        underline: {
          tab: {
            fontWeight: '500',
            fontSize: 'sm',
            color: 'neutral.500',
            borderBottom: '2px solid transparent',
            transition: 'all 0.15s ease',
            px: 4,
            py: 2,
            _selected: {
              color: 'brand.600',
              fontWeight: '600',
              borderBottomColor: 'brand.600',
            },
            _hover: {
              color: 'neutral.700',
            },
          },
          tablist: {
            borderBottom: '1px solid',
            borderColor: 'neutral.200',
          },
        },
        adminLine: {
          tab: {
            fontWeight: '500',
            fontSize: 'sm',
            color: 'neutral.500',
            borderBottom: '2px solid transparent',
            transition: 'all 0.15s ease',
            _selected: {
              color: 'neutral.800',
              fontWeight: '600',
              borderBottomColor: 'neutral.800',
            },
            _hover: {
              color: 'neutral.700',
            },
          },
        },
      },
    },

    Heading: {
      baseStyle: {
        letterSpacing: '-0.02em',
        color: 'neutral.800',
      },
    },
  },
});

export default theme;
